import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, updateDoc, Timestamp, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import Loader from "./Loader";

const OFFICE_LOCATION = {
  lat: 12.9676,
  lng: 79.9419,
};




const logout = async (navigate) => {
  await signOut(auth);
  navigate("/EmpLogin", { replace: true });
};

const MAX_DISTANCE_KM = 5;

function EmployeeAttendance() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [distance, setDistance] = useState(null);
  const [coords, setCoords] = useState(null);
  const [attendance, setAttendance] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [empName, setEmpName] = useState("");
  const [locationReady, setLocationReady] = useState(false);


  const today = new Date().toISOString().slice(0, 10);

  // Notifications State
  const [notifications, setNotifications] = useState([]);

  /* ---------- AUTH ---------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) navigate("/EmpLogin", { replace: true });
      else setUser(u);
      setAuthLoading(false);

    });
    return () => unsub();
  }, [navigate]);

  /*USER NAME FETCH*/
  useEffect(() => {
    if (!user) return;

    const fetchEmployee = async () => {
      try {
        // 🔁 change collection name if yours is different
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          setEmpName(snap.data().name); // 👈 field name
        } else {
          setEmpName("Null");
        }
      } catch (err) {
        console.error("Error fetching employee name", err);
        setEmpName("Employee");
      }
    };

    fetchEmployee();
  }, [user]);


  /* ---------- DISTANCE ---------- */
  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  /* ---------- LOCATION ---------- */
  useEffect(() => {
    navigator.geolocation.getCurrentPosition((pos) => {
      const { latitude, longitude } = pos.coords;
      setCoords({ lat: latitude, lng: longitude });

      const km = getDistanceKm(
        latitude,
        longitude,
        OFFICE_LOCATION.lat,
        OFFICE_LOCATION.lng
      );
      setDistance(Number(km.toFixed(2)));
      setLocationReady(true);
    },
      () => {
        setLocationReady(false);
      }
    );
  }, []);

  /* ---------- LOAD ATTENDANCE ---------- */
  useEffect(() => {
    if (!user) return;

    const load = async () => {
      const ref = doc(db, "attendance", `${user.uid}_${today}`);
      const snap = await getDoc(ref);
      if (snap.exists()) setAttendance(snap.data());
    };

    load();
  }, [user, today]);

  /* ---------- LOAD NOTIFICATIONS ---------- */
  useEffect(() => {
    if (!user) return;

    const fetchNotifications = async () => {
      try {
        const q = query(
          collection(db, "SYSTEM_NOTIFICATIONS"),
          where("targetUid", "==", user.uid),
          where("isRead", "==", false)
        );
        const snap = await getDocs(q);
        const notifs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort by date created desc
        notifs.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds);
        setNotifications(notifs);
      } catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };

    fetchNotifications();
  }, [user]);

  /* ---------- MARK NOTIFICATION READ ---------- */
  const markAsRead = async (notifId) => {
    try {
      await updateDoc(doc(db, "SYSTEM_NOTIFICATIONS", notifId), {
        isRead: true,
        readAt: Timestamp.now()
      });
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (err) {
      console.error("Error marking notification read:", err);
    }
  };

  /* ---------- CHECK-IN ---------- */
  const checkIn = async () => {
    if (loading || distance > MAX_DISTANCE_KM || !coords) return;

    setLoading(true);
    setMessage("");

    const data = {
      empId: user.uid,
      uid: user.uid,
      date: today,
      checkInTime: Timestamp.now(),
      checkInDistance: distance,
      checkInLocation: coords,
    };

    await setDoc(doc(db, "attendance", `${user.uid}_${today}`), data);

    setAttendance(data);
    setMessage("✅ Check-In Successful");
    setLoading(false);
  };

  /* ---------- CHECK-OUT ---------- */
  const checkOut = async () => {
    if (loading || !coords) return;

    setLoading(true);
    setMessage("");

    const updated = {
      ...attendance,
      checkOutTime: Timestamp.now(),
      checkOutDistance: distance,
      checkOutLocation: coords,
    };

    await updateDoc(doc(db, "attendance", `${user.uid}_${today}`), {
      checkOutTime: updated.checkOutTime,
      checkOutDistance: updated.checkOutDistance,
      checkOutLocation: updated.checkOutLocation,
    });

    setAttendance(updated);
    setMessage("✅ Check-Out Successful");
    setLoading(false);
  };

  /* ---------- UI ---------- */
  if (authLoading) return <Loader />;
  if (!user) return null;

  let totalHours = 0;
  if (attendance?.checkInTime && attendance?.checkOutTime) {
    totalHours =
      (attendance.checkOutTime.seconds -
        attendance.checkInTime.seconds) /
      3600;
  }

  return (
    <div className="container mt-4 text-center">
      <h3>Employee Attendance</h3>

      <p>
        Distance from Office: <b>{distance ?? "..."} km</b>
      </p>

      {/* NOTIFICATIONS BANNER */}
      {notifications.length > 0 && (
        <div className="mb-4">
          {notifications.map(notif => (
            <div key={notif.id} className="alert alert-warning border border-warning shadow-sm d-flex justify-content-between align-items-center text-start mb-2 animate__animated animate__headShake">
              <div>
                <strong><i className="bi bi-exclamation-triangle-fill me-2"></i>{notif.title}</strong><br />
                <span className="small">{notif.message}</span>
              </div>
              <button className="btn btn-sm btn-outline-dark" onClick={() => markAsRead(notif.id)}>
                Got it
              </button>
            </div>
          ))}
        </div>
      )}

      {message && <div className="alert alert-success">{message}</div>}

      {!attendance?.checkInTime && (
        <button
          className="btn btn-success"
          disabled={loading || !locationReady || distance > MAX_DISTANCE_KM}
          onClick={checkIn}
        >
          {loading ? <span className="spinner-border spinner-border-sm" /> : "Check In"}
        </button>
      )}

      {attendance?.checkInTime && !attendance?.checkOutTime && (
        <button
          className="btn btn-warning"
          disabled={loading}
          onClick={checkOut}
        >
          {loading ? <span className="spinner-border spinner-border-sm" /> : "Check Out"}
        </button>
      )}

      {attendance?.checkOutTime && (
        <div className="alert alert-info mt-3 text-start">
          <p><b>Employee Name:</b> {empName}</p>
          <p>Check-In: {attendance.checkInTime.toDate().toLocaleTimeString()}</p>
          <p>Check-Out: {attendance.checkOutTime.toDate().toLocaleTimeString()}</p>
          <p>Total Hours Worked: {totalHours.toFixed(2)} hrs</p>
          <button className="btn btn-danger" onClick={() => logout(navigate)}>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default EmployeeAttendance;
